import { defineStore } from "pinia";
import { privateBoaHttp } from "@/boa-api-client";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { saveAs } from "file-saver";
import { useAuthStore, useAwardStore } from "@/stores";

async function downloadReadableStream(
  stream: ReadableStream,
  filename: string
) {
  const response = new Response(stream);
  const blob = await response.blob();
  saveAs(blob, filename);
}

type ExportStatus = {
  jobId?: string;
  s3Key?: string;
  generated?: string;
  error?: string;
};

type State = {
  referrals: any;
  "certificates-export": ExportStatus & { key: string; awardYear: number };
  "contacts-export": ExportStatus & { key: string; awardYear: number };
  "magazine-mailing-list-export": ExportStatus & {
    key: string;
    awardYear: number;
  };
  "magazine-category-export": ExportStatus & { key: string; awardYear: number };
  "magazine-finalist-export": ExportStatus & { key: string; awardYear: number };
  "magazine-layout-export": ExportStatus & { key: string; awardYear: number };
  "nominee-contacts-export": ExportStatus & { key: string; awardYear: number };
  "nominees-website-export": ExportStatus & {
    key: string;
    awardYear: number;
  };
  "nominee-letters-export": ExportStatus & {
    key: string;
    awardYear: number;
  };
  "finalists-website-export": ExportStatus & { key: string; awardYear: number };
  "finalist-contacts-export": ExportStatus & { key: string; awardYear: number };
  "finalist-letters-export": ExportStatus & {
    key: string;
    awardYear: number;
  };
  "plaque-orders-export": ExportStatus & { key: string };
  "slideshow-export": ExportStatus & { key: string; awardYear: number };
  "winners-web-export": ExportStatus & { key: string; awardYear: number };
  "sponsorship-interest-contacts-export": ExportStatus & {
    key: string;
    awardYear: number;
  };
  "awards-ceremony-attendees-export": ExportStatus & {
    key: string;
    awardYear: number;
  };
};

export default defineStore("exports", {
  state: (): State => {
    const awardStore = useAwardStore();
    const awardYear = awardStore.awardYear;
    return {
      referrals: {
        key: "referrals",
        awardYear,
      },
      "certificates-export": {
        key: "certificates-export",
        awardYear,
      },
      "contacts-export": {
        key: "contacts-export",
        awardYear,
      },
      "magazine-mailing-list-export": {
        key: "magazine-mailing-list-export",
        awardYear,
      },
      "magazine-category-export": {
        key: "magazine-category-export",
        awardYear,
      },
      "nominee-contacts-export": {
        key: "nominee-contacts-export",
        awardYear,
      },
      "magazine-finalist-export": {
        key: "magazine-finalist-export",
        awardYear,
      },
      "magazine-layout-export": {
        key: "magazine-layout-export",
        awardYear,
      },
      "nominees-website-export": {
        key: "nominees-website-export",
        awardYear,
      },
      "nominee-letters-export": {
        key: "nominee-letters-export",
        awardYear,
      },
      "finalists-website-export": {
        key: "finalists-website-export",
        awardYear,
      },
      "finalist-contacts-export": {
        key: "finalist-contacts-export",
        awardYear,
      },
      "finalist-letters-export": {
        key: "finalist-letters-export",
        awardYear,
      },
      "plaque-orders-export": {
        key: "plaque-orders-export",
      },
      "slideshow-export": {
        key: "slideshow-export",
        awardYear,
      },
      "winners-web-export": {
        key: "winners-web-export",
        awardYear,
      },
      "sponsorship-interest-contacts-export": {
        key: "sponsorship-interest-contacts-export",
        awardYear,
      },
      "awards-ceremony-attendees-export": {
        key: "awards-ceremony-attendees-export",
        awardYear,
      },
    };
  },
  actions: {
    async downloadExport(_export: string) {
      const { s3Key } = this[_export as keyof State];
      if (!s3Key) {
        console.error("S3 Key missing");
      }
      const authStore = useAuthStore();
      const credentials = authStore.credentials;
      const client = new S3Client({
        region: "us-west-2",
        credentials: {
          accessKeyId: credentials.AccessKeyId!,
          secretAccessKey: credentials.SecretKey!,
          sessionToken: credentials.SessionToken,
        },
      });
      const response: any = await client.send(
        new GetObjectCommand({
          Bucket: import.meta.env.VITE_EXPORTS_BUCKET,
          Key: s3Key,
          ResponseCacheControl: "no-cache",
        })
      );
      downloadReadableStream(response.Body, s3Key);
    },
    async getExportStatus(_export: string) {
      try {
        const searchParams = new URLSearchParams();
        if (this[_export as keyof State].awardYear) {
          searchParams.set(
            "awardYear",
            this[_export as keyof State].awardYear.toString()
          );
        }
        const response = await privateBoaHttp
          .get(`exports/${_export}`, {
            searchParams,
          })
          .json<{ export: ExportStatus; success: boolean }>();
        this[_export as keyof State] = {
          ...this[_export as keyof State],
          ...response.export,
        };
        this[_export as keyof State].jobId = response.export.jobId;
        this[_export as keyof State].error = response.export.error;
        return response.export;
      } catch (error: any) {
        const { message } = await (error as any).response.json();
        return { message };
      }
    },
    async generateExport(_export: string) {
      try {
        const response: { jobId: string } = await privateBoaHttp
          .post(`exports/${_export}`, {
            searchParams: {
              awardYear: this[_export as keyof State].awardYear,
            },
          })
          .json();
        return { jobId: response.jobId };
      } catch (error: any) {
        const { message } = await (error as any).response.json();
        return { message };
      }
    },
    clear() {
      this.$reset();
    },
  },
});
